import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { User } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string, phoneNumber: string, userType: 'loan_originator' | 'real_estate_agent', nmls?: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch user profile from Firestore userProfiles collection
        const profileRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(profileRef);
        if (userDoc.exists()) {
          const profile = userDoc.data() as User;
          // Sync Firebase Auth emailVerified flag to Firestore if it changed
          if (user.emailVerified && !profile.emailVerified) {
            try {
              await updateDoc(profileRef, { emailVerified: true });
              profile.emailVerified = true;
            } catch {
              // Non-blocking
            }
          }
          setUserProfile(profile);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email: string, password: string, firstName: string, lastName: string, phoneNumber: string, userType: 'loan_originator' | 'real_estate_agent', nmls?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user profile in Firestore userProfiles collection
    const userProfile: User = {
      uid: user.uid,
      email,
      firstName,
      lastName,
      phoneNumber,
      userType,
      verificationStatus: 'pending',
      phoneVerified: true,   // phone must be verified to reach this step
      emailVerified: false,  // will sync to true when user clicks the verification link
      createdAt: new Date()
    };

    // Only add nmls field if it has a value
    if (nmls) {
      userProfile.nmls = nmls;
    }

    await setDoc(doc(db, 'userProfiles', user.uid), userProfile);
    setUserProfile(userProfile);

    // Send Firebase email verification link
    try {
      await sendEmailVerification(user);
    } catch (err) {
      // Non-blocking: log but don't fail signup
      console.warn('Could not send email verification:', err);
    }
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    login,
    signup,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
